import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session as SupabaseSession, User } from "@supabase/supabase-js";
import { supabase, isSupabaseEnabled } from "./supabaseClient";

// Per-user feature flags. Persisted as `users.features` jsonb in
// Supabase; defaults to all-off in production. Flipped per-account
// for discovery-partner demos via the SQL in supabase/README.md.
export interface FeatureFlags {
  holyWeek: boolean;
}

export interface Session {
  userId: string;
  parishId: string;
  parishName: string;
  email: string;
  displayName: string;
  features: FeatureFlags;
}

// Stub used when Supabase isn't configured (local dev, pre-
// provisioning). Mirrors the OLG profile so the dashboard is
// reachable without the magic-link dance.
const STUB_SESSION: Session = {
  userId: "stub-user-colleen",
  parishId: "stub-parish-olg",
  parishName: "Our Lady of Grace",
  email: "colleen@example.com",
  displayName: "Colleen",
  features: {
    // Default ON in dev so the Holy Week tab is reachable without
    // DevTools. Real Supabase users default to false — flipped on
    // per-account when Michael demos to a discovery partner.
    holyWeek: true,
  },
};

const SessionContext = createContext<Session | null>(null);
const JwtContext = createContext<string | null>(null);

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) {
    throw new Error(
      "useSession must be used inside <SessionProvider>. The provider wraps <App /> in main.tsx.",
    );
  }
  return s;
}

// Current Supabase access token, or null in stub mode. Used by
// frontend/src/lib/api.ts to attach `Authorization: Bearer …` to
// /api/* requests.
export function useJwt(): string | null {
  return useContext(JwtContext);
}

type BootState =
  // First session fetch in flight.
  | { kind: "loading" }
  // Supabase not configured — using STUB_SESSION.
  | { kind: "stub"; session: Session }
  // Supabase signed in, real session loaded.
  | { kind: "ready"; session: Session; jwt: string }
  // Supabase configured but no session — provider renders the
  // signed-out fallback (typically the SignIn view).
  | { kind: "signed-out" };

export function SessionProvider({
  children,
  fallback,
  signedOutFallback,
}: {
  children: ReactNode;
  fallback: ReactNode;
  signedOutFallback: ReactNode;
}) {
  const [state, setState] = useState<BootState>(() =>
    isSupabaseEnabled ? { kind: "loading" } : { kind: "stub", session: STUB_SESSION },
  );

  useEffect(() => {
    if (!isSupabaseEnabled || !supabase) return;

    let cancelled = false;

    async function applySession(supaSession: SupabaseSession | null) {
      if (cancelled) return;
      if (!supaSession) {
        setState({ kind: "signed-out" });
        return;
      }
      const session = await hydrateSession(supaSession.user);
      if (cancelled) return;
      setState({ kind: "ready", session, jwt: supaSession.access_token });
    }

    supabase.auth.getSession().then(({ data }) => applySession(data.session));

    const { data: sub } = supabase.auth.onAuthStateChange((_event, supaSession) => {
      void applySession(supaSession);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (state.kind === "loading") return <>{fallback}</>;
  if (state.kind === "signed-out") return <>{signedOutFallback}</>;

  const jwt = state.kind === "ready" ? state.jwt : null;
  return (
    <SessionContext.Provider value={state.session}>
      <JwtContext.Provider value={jwt}>{children}</JwtContext.Provider>
    </SessionContext.Provider>
  );
}

// Load the parish-scoped session details for a signed-in Supabase
// user. The users row carries parish_id + features; the joined
// parishes row carries the display name. RLS scopes the row to the
// authenticated user, so an unprovisioned user (signed in via magic
// link but with no users row yet) returns a fallback keyed off the
// auth identity alone.
async function hydrateSession(user: User): Promise<Session> {
  // users.display_name (the parish-controlled name) is the canonical
  // source. Auth metadata (OAuth profile, etc.) is a fallback when
  // the users row doesn't carry one. NEVER fall back to the email
  // local-part — better to show no name than "capuscas".
  const authDerivedName = pickAuthDisplayName(user);
  const fallback: Session = {
    userId: user.id,
    parishId: "unknown",
    parishName: "Your parish",
    email: user.email ?? "",
    displayName: authDerivedName,
    features: { holyWeek: false },
  };

  if (!supabase) return fallback;

  const { data, error } = await supabase
    .from("users")
    .select("parish_id, display_name, features, parishes(name)")
    .eq("id", user.id)
    .maybeSingle<UserRow>();

  if (error || !data) {
    console.warn("[session] no users row for", user.id, error?.message ?? "");
    return fallback;
  }

  return {
    userId: user.id,
    parishId: data.parish_id ?? "unknown",
    parishName: data.parishes?.name ?? "Your parish",
    email: user.email ?? "",
    displayName: data.display_name?.trim() || authDerivedName,
    features: {
      holyWeek: Boolean(data.features?.holyWeek),
    },
  };
}

interface UserRow {
  parish_id: string | null;
  display_name: string | null;
  features: { holyWeek?: boolean } | null;
  parishes: { name: string } | null;
}

function pickAuthDisplayName(user: User): string {
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const full = typeof meta.full_name === "string" ? meta.full_name : null;
  if (full) return full;
  const first = typeof meta.first_name === "string" ? meta.first_name : null;
  if (first) return first;
  return "";
}
